import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Send, Clock, MessageCircle, Eye, RotateCw } from "lucide-react";

export default function Whatsapp() {
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [tpl, setTpl] = useState("");
  const [body, setBody] = useState("Hello {customer_name}, {business_name} has curated something special for you.");
  const [tagSeg, setTagSeg] = useState("all");
  const [scheduleAt, setScheduleAt] = useState("");
  const [sending, setSending] = useState(false);
  const [jobDetail, setJobDetail] = useState(null);
  const [loadingJob, setLoadingJob] = useState(null);
  const [pickedIds, setPickedIds] = useState([]);
  const [actingOn, setActingOn] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [uploadingImg, setUploadingImg] = useState(false);

  const viewJob = async (jid) => {
    setLoadingJob(jid);
    try {
      const { data } = await api.get(`/whatsapp/jobs/${jid}`);
      setJobDetail(data);
    } catch (e) { toast.error("Could not load broadcast details"); }
    finally { setLoadingJob(null); }
  };

  useEffect(() => {
    api.get("/templates").then(r=>setTemplates(r.data));
    api.get("/customers").then(r=>setCustomers(r.data));
    api.get("/whatsapp/jobs").then(r=>setJobs(r.data));
  }, []);

  const recipients = useMemo(() => {
    if (tagSeg === "specific") return customers.filter(c => pickedIds.includes(c.id));
    return tagSeg==="all" ? customers : customers.filter(c=>c.tag===tagSeg);
  }, [customers, tagSeg, pickedIds]);

  const togglePicked = (id) => setPickedIds(p => p.includes(id) ? p.filter(x=>x!==id) : [...p, id]);

  const cancelJob = async (jid) => {
    setActingOn(jid);
    try {
      await api.post(`/whatsapp/jobs/${jid}/cancel`);
      toast.success("Broadcast cancelled");
      const { data: j } = await api.get("/whatsapp/jobs");
      setJobs(j);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
    finally { setActingOn(null); }
  };

  const sendJobNow = async (jid) => {
    setActingOn(jid);
    try {
      await api.post(`/whatsapp/jobs/${jid}/send-now`);
      toast.success("Sending now…");
      const { data: j } = await api.get("/whatsapp/jobs");
      setJobs(j);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setActingOn(null); }
  };

  const resendFailed = async (jid) => {
    setActingOn(jid);
    try {
      const { data } = await api.post(`/whatsapp/jobs/${jid}/resend-failed`);
      toast.success(`Resent to ${data.retried} recipient(s) — ${data.sent} sent, ${data.failed} still failed`);
      const { data: j } = await api.get("/whatsapp/jobs");
      setJobs(j);
      if (jobDetail?.id === jid) viewJob(jid);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend"); }
    finally { setActingOn(null); }
  };

  const singleRecipientName = (j) => {
    if (j.recipient_ids && j.recipient_ids.length === 1) {
      return customers.find(c => c.id === j.recipient_ids[0])?.name || null;
    }
    return null;
  };

  const preview = useMemo(() => {
    const sample = customers[0]?.name || "Priya";
    return body.replace(/\{customer_name\}/g, sample).replace(/\{business_name\}/g, "Gaze Decor");
  }, [body, customers]);

  const selectedTpl = useMemo(() => templates.find(t => t.id === tpl), [templates, tpl]);

  const useTemplate = (id) => {
    setTpl(id);
    const t = templates.find(x=>x.id===id);
    if (t) setBody(t.body);
    setImageUrl(""); // a different template may not need (or may need a different) photo
  };

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploadingImg(true);
    try {
      const { data } = await api.post("/whatsapp/broadcast/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(data.url);
      toast.success("Photo attached");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not upload photo"); }
    finally { setUploadingImg(false); }
  };

  const send = async () => {
    if (!body.trim()) return toast.error("Message is empty");
    if (!tpl) return toast.error("Select an approved Meta template — broadcasts can't send as free text");
    if (tagSeg === "specific" && pickedIds.length === 0) return toast.error("Pick at least one customer");
    if (selectedTpl?.has_image_header && !imageUrl) return toast.error("This template needs a photo attached before sending");
    setSending(true);
    try {
      const payload = { body, template_id: tpl };
      if (imageUrl) payload.image_url = imageUrl;
      if (tagSeg === "specific") payload.recipient_ids = pickedIds;
      else if (tagSeg !== "all") payload.tag = tagSeg;
      if (scheduleAt) payload.scheduled_at = new Date(scheduleAt).toISOString();
      const { data } = await api.post("/whatsapp/broadcast", payload);
      toast.success(data.status === "scheduled" ? `Scheduled to ${data.total} customers` : `Sending to ${data.total} customers…`);
      setImageUrl("");
      const { data: j } = await api.get("/whatsapp/jobs");
      setJobs(j);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Marketing</p>
        <h1 className="font-serif-elegant text-5xl mt-1">WhatsApp Broadcast</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2 bg-white border-border space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Use template (optional)</Label>
              <Select value={tpl} onValueChange={useTemplate}>
                <SelectTrigger data-testid="wa-template-select"><SelectValue placeholder="Choose template"/></SelectTrigger>
                <SelectContent>
                  {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Send to segment</Label>
              <Select value={tagSeg} onValueChange={setTagSeg}>
                <SelectTrigger data-testid="wa-segment-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="vip">VIP</SelectItem>
                  <SelectItem value="one-time">One-time</SelectItem>
                  <SelectItem value="specific">Specific customer(s)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {tagSeg === "specific" && (
            <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-1" data-testid="wa-specific-picker">
              {customers.length === 0 && <p className="text-xs text-muted-foreground">No customers yet.</p>}
              {customers.map(c => (
                <label key={c.id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                  <input type="checkbox" checked={pickedIds.includes(c.id)} onChange={()=>togglePicked(c.id)} data-testid={`wa-pick-${c.id}`} />
                  <span>{c.name}</span>
                  {c.phone && <span className="text-xs text-muted-foreground">— {c.phone}</span>}
                </label>
              ))}
            </div>
          )}
          {selectedTpl?.has_image_header && (
            <div>
              <Label>Offer photo (required for this template)</Label>
              <div className="flex items-center gap-3">
                <Input type="file" accept="image/png,image/jpeg,image/webp" disabled={uploadingImg}
                       onChange={e=>uploadImage(e.target.files?.[0])} data-testid="wa-image-input" />
                {uploadingImg && <span className="text-xs text-muted-foreground">Uploading…</span>}
              </div>
              {imageUrl && <img src={imageUrl} alt="Offer" className="mt-2 h-24 rounded-md border border-border object-cover" />}
            </div>
          )}
          <div>
            <Label>Message body</Label>
            <Textarea rows={7} value={body} onChange={e=>setBody(e.target.value)} disabled={!!tpl} data-testid="wa-body" />
            <p className="text-xs text-muted-foreground mt-1">
              {tpl
                ? "This is your approved Meta template's fixed wording — only {customer_name} and {business_name} get filled in per recipient; the rest can't be edited here."
                : <>Placeholders: <code>{"{customer_name}"}</code>, <code>{"{business_name}"}</code> — select an approved template above before sending.</>}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1"><Clock size={12}/> Schedule (optional)</Label>
              <Input type="datetime-local" value={scheduleAt} onChange={e=>setScheduleAt(e.target.value)} data-testid="wa-schedule" />
            </div>
            <div className="flex items-end justify-end">
              <Button onClick={send} disabled={sending || !tpl} className="bg-primary hover:bg-[#984D38] w-full sm:w-auto" data-testid="wa-send-btn">
                <Send size={14} className="mr-2"/>{scheduleAt ? "Schedule Broadcast" : `Send to ${recipients.length}`}
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-[#E7F0E4] border-[#c7dcbb]">
          <div className="flex items-center gap-2 mb-2 text-[#2f5030]"><MessageCircle size={14}/><span className="text-xs uppercase tracking-wider">Preview</span></div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-[#dcead0]">
            {selectedTpl?.has_image_header && imageUrl && (
              <img src={imageUrl} alt="Offer" className="w-full h-32 object-cover rounded-md mb-2" />
            )}
            <p className="whitespace-pre-wrap text-sm">{preview}</p>
            <p className="text-[10px] text-muted-foreground text-right mt-2">delivered ✓✓</p>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Will reach {recipients.length} recipient(s).</p>
        </Card>
      </div>

      <Card className="p-6 bg-white border-border">
        <h3 className="font-serif-elegant text-2xl mb-4">Recent Broadcasts</h3>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr><th className="py-2">Started</th><th>Recipients</th><th>Sent</th><th>Failed</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {jobs.length===0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No broadcasts yet.</td></tr>}
            {jobs.map(j => (
              <tr key={j.id} className="border-t hairline">
                <td className="py-2">{j.created_at?.slice(0,16).replace("T"," ")}</td>
                <td>{singleRecipientName(j) || j.total}</td>
                <td className="text-green-700">{j.sent}</td>
                <td className="text-red-700">{j.failed}</td>
                <td><Badge variant="outline" className="capitalize">{j.status}</Badge></td>
                <td className="text-right space-x-1">
                  {j.status === "scheduled" && (
                    <>
                      <Button variant="ghost" size="sm" onClick={()=>sendJobNow(j.id)} disabled={actingOn===j.id} data-testid={`send-now-${j.id}`}>Send now</Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={()=>cancelJob(j.id)} disabled={actingOn===j.id} data-testid={`cancel-job-${j.id}`}>Cancel</Button>
                    </>
                  )}
                  {j.status === "done" && (j.failed > 0 || j.sent > 0) && (
                    <Button variant="ghost" size="sm" onClick={()=>viewJob(j.id)} disabled={loadingJob===j.id} data-testid={`view-job-${j.id}`}>
                      <Eye size={14} className="mr-1"/>Why?
                    </Button>
                  )}
                  {j.status === "done" && j.failed > 0 && (
                    <Button variant="ghost" size="sm" onClick={()=>resendFailed(j.id)} disabled={actingOn===j.id} data-testid={`resend-failed-${j.id}`}>
                      <RotateCw size={14} className={`mr-1 ${actingOn===j.id?"animate-spin":""}`}/>Resend failed
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!jobDetail} onOpenChange={(o)=>{ if(!o) setJobDetail(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif-elegant text-2xl">Broadcast Details</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {jobDetail?.results?.length ? jobDetail.results.map((r, i) => (
              <div key={i} className="flex items-start justify-between gap-3 border-b hairline pb-2 text-sm">
                <div>
                  <p className="font-medium">{r.to || r.customer_id}</p>
                  {!r.ok && <p className="text-red-700 text-xs mt-0.5">{typeof r.error === "string" ? r.error : JSON.stringify(r.error)}</p>}
                </div>
                <Badge variant="outline" className={r.ok ? "bg-green-100 text-green-800 border-green-200" : "bg-red-100 text-red-800 border-red-200"}>
                  {r.ok ? "Sent" : "Failed"}
                </Badge>
              </div>
            )) : <p className="text-sm text-muted-foreground py-6 text-center">No per-recipient details recorded for this broadcast.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
